<script setup>
import {ref, computed, onMounted, defineAsyncComponent} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
/***/
import User from '@classes/User';
/***/
import { useUserStore } from '@stores/user';
import { useApplicationStore } from '@stores/application';
import { useHistoryStore } from '@stores/history';
/***/
import { useAuthUser } from '@compositions/Auth';

// Composants
const Loader = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/UI/Loader'));
const Layout = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Layouts/Authenticated'));
const AgentList = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/List/AgentList'));
const EditUserPlanning = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/Item/Planning/EditUserPlanning'));
const EditUserInternalForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/Forms/EditUserInternalForm'));
const EditUserCustomerForm = defineAsyncComponent(() => import(/* webpackChunkName: "components" */ '@components/Partial/Admin/Users/Forms/EditUserCustomerForm'));
const Icon = defineAsyncComponent(() => import(/* webpackChunkName: "components" */'@components/UI/Icon'));

const router = useRouter();
const route = useRoute();
const { t } = useI18n();

// Store
const applicationStore = useApplicationStore();
const userStore = useUserStore();
const historyStore = useHistoryStore();

const userId = parseInt(route.params.userId);
const targetUser = computed(() => userStore.getUserById(userId));

// Vrai si l'utilisateur a été redirigé après avoir créé un nouvel utilisateur
const fromCreation = computed(() => (route.query['from-create'] || null) !== null);
const creationSuccessMessageRef = ref(null);

// Vrai si l compte de l'utilisateur a été mis à jour
const userSaved = ref(false);
// Référence sur l'élément DOM du message de succès
const editionSuccessMessageRef = ref(null);

const currentApplication = computed(() => applicationStore.getCurrent);

// ready est vrai une fois l'utilisateur chargé.
// Cela permet d'afficher un loader le temps du chargement des données
const ready = ref(false);

// Vrai s'il y a eu une erreur pendant le chargement de l'utilisateur
const loadingTargetUserError = ref(false);

const { authUser, authUserHasPermission } = useAuthUser({});
const userTypeParam = route.params.userType;
const pageTitle = ref(null);
const pageDescription = ref(null);

const userTypeCamelCase = User.getCamelTypeBySlug(userTypeParam);

// Redirection vers une page d'erreur
const redirectNotFound = (() => {
    router.push({
        name: 'error',
        params: {
            statusCode: 404
        }
    });
});

// Méthode appelée si l'utilisateur vient de créer un nouvel utilisateur
const onCreationSuccess = (() => {
    setTimeout(() => {
        if(creationSuccessMessageRef.value !== null) {
            creationSuccessMessageRef.value.scrollIntoView({ behavior: 'smooth' });
        }
    }, 200);

    setTimeout(() => {
        historyStore.setMustPreserveScrollPosition(true);
        // Suppression du paramètre en query
        const query = Object.assign({}, route.query);
        delete query['from-create'];
        router.replace({
            query
        });

    }, 5200);
});

// Initialisation de la page : vérification des permissions et titre de la page
const initPage = (() => {
    if(! targetUser.value.canEdit) {
        redirectNotFound();
    } else {

        const titleKey = 'page.admin.users.:type.edit.title'.replace(':type', userTypeCamelCase);
        const descriptionKey = 'page.admin.users.:type.edit.description'.replace(':type', userTypeCamelCase);
        document.title = t(titleKey, {
            name: targetUser.value.fullName
        }) + ' - ' + currentApplication.value.name;
        pageTitle.value = t(titleKey, {
            name: targetUser.value.fullName
        });
        pageDescription.value = t(descriptionKey, {
            name: targetUser.value.fullName
        });

        // Cache le succès de création après quelques secondes
        if(fromCreation.value !== null) {
            onCreationSuccess();
        }
    }
});

const canDisplayPlanning = (userTypeCamelCase !== 'customer' && authUserHasPermission('manage agent planning'));

// Chargement de l'utilisateur et initialisation de la page
const loadUser = (() => {

    const withParam = [
        'role',
        'informations',
        'settings'
    ];

    if(userTypeCamelCase === 'customer') {
        withParam.push('society', 'socialNetworkIdentities', 'graphicCharterFiles', 'notificationSettings', 'sector');
    } else {
        withParam.push('contract');
    }

    if(userTypeCamelCase === 'teamManager') {
        withParam.push('agents');
    }

    if(canDisplayPlanning) {
        withParam.push('planning');
    }

    userStore.loadUserById(userId, userTypeCamelCase, withParam).then((withParam) => {
        initPage();
    }).catch((e) => {
        loadingTargetUserError.value = true;
    }).finally(() => {
        ready.value = true;
    });
});

onMounted(() => {
    const requiredPermission = User.getEditPermissionByType(userTypeCamelCase);
    const canEditPermission = (requiredPermission !== null && authUserHasPermission(requiredPermission));

    if(! canEditPermission) {
        redirectNotFound();
    } else {
        loadUser();
    }
});

// Méthode à exécuter lors de la mise à jour d'un utilisateur
const onUpdateUser = ((newUser) => {
    userStore.updateUser(newUser);

    userSaved.value = true;

    // Scroll au message de succès après 200ms (le temps de mettre à jour le DOM)
    setTimeout(() => {
        if(editionSuccessMessageRef.value !== null) {
            editionSuccessMessageRef.value.scrollIntoView({ behavior: "smooth" });
        }
    }, 200);

    // Cache le message de succès après quelques secondes
    setTimeout(() => {
        userSaved.value = false;
    }, 5200);
});

// Retourne le nom du composant pour le formulaire d'édition
const getFormComponentName = (() => {
    return (userTypeCamelCase === 'customer') ? EditUserCustomerForm : EditUserInternalForm;
});

// Gestion des onglets
const tabMenuKeys = (() => {
    const keys = [
        'informations'
    ];
    if(canDisplayPlanning) {
        keys.push('planning');
    }
    return keys;
});
const canDisplayTabMenu = (() => {
    return (tabMenuKeys().length > 1);
});
const activeTab = ref(tabMenuKeys().at(0));

// Retourne le nom de l'icône à utiliser pour l'onglet dont le type est en paramètre
const getIconByTab = ((tab) => {
    const iconNames = {
        informations: 'edit',
        planning: 'calendar',
    };

    return (iconNames[tab] ?? null);
});
</script>

<template>
    <layout wrapperContentClasses="wrapper_edit-add">
        <loader v-if="! ready"></loader>
        <template v-else>
            <p
                v-if="loadingTargetUserError || ! targetUser"
                class="generic-message generic-message-error">
                {{ $t('page.admin.users.edit.loading') }}
            </p>
            <template v-else>
                <div class="content_header">
                    <div class="content-header_title">
                        <h1 class="first-title">{{ pageTitle }}</h1>
                        <p>{{ pageDescription }}</p>
                    </div>
                </div>

                <div class="content_tab">
                    <div
                        v-if="canDisplayTabMenu()"
                        class="tab"
                    >
                        <button
                            type="button"
                            :class="{ active: activeTab === 'informations'}"
                            class="tab-button-with-icon"
                            @click="activeTab = 'informations'"
                        >
                            <icon
                                v-if="getIconByTab('informations')"
                                :name="getIconByTab('informations')"
                            ></icon>
                            <span>{{ $t('page.admin.users.edit.tabs.informations') }}</span>
                        </button>
                        <button
                            type="button"
                            :class="{ active: activeTab === 'planning'}"
                            class="tab-button-with-icon"
                            @click="activeTab = 'planning'"
                        >
                            <icon
                                v-if="getIconByTab('planning')"
                                :name="getIconByTab('planning')"
                            ></icon>
                            <span>{{ $t('page.admin.users.edit.tabs.planning') }}</span>
                        </button>
                    </div>
                </div>


                <p
                    v-if="fromCreation"
                    ref="creationSuccessMessageRef"
                    class="generic-message generic-message-success"
                >
                    {{ $t('page.admin.users.edit.created', { name: targetUser.fullName }) }}
                </p>

                <p
                    v-if="userSaved"
                    ref="editionSuccessMessageRef"
                    class="generic-message generic-message-success"
                >
                    {{ $t('page.admin.users.edit.updated', { name: targetUser.fullName }) }}
                </p>


                <div v-show="activeTab === 'informations'">
                    <component
                        :is="getFormComponentName()"
                        :user-type="userTypeCamelCase"
                        :user="targetUser"
                        v-on:user-save="onUpdateUser($event)"
                    ></component>
                </div>

                <template v-if="canDisplayPlanning">
                    <div v-show="activeTab === 'planning'">
                        <edit-user-planning :user="targetUser"></edit-user-planning>
                    </div>
                </template>

                <template
                    v-if="userTypeCamelCase === 'teamManager' && targetUser.agents !== undefined && targetUser.agents.length > 0"
                >
                    <div class="wrapper_content no-top-padding">
                    <div class="content_subheader">
                        <div class="content-subheader_title">
                            <h4 class="fourth-title">{{ $t('page.admin.users.teamManager.edit.agents', {
                                name: targetUser.fullName
                            }) }}</h4>
                        </div>
                    </div>
                    <agent-list
                        :items="targetUser.agents"
                        :sort="{ order: 'created_at', direction: 'asc' }"
                    ></agent-list>
                    </div>
                </template>
            </template>
        </template>
    </layout>
</template>
